import React from "react";

type CategoryKey = "switch" | "aircurtain" | "airdriver" | "irblaster";

interface SelectedItem {
  isOn: boolean;
  isDimmable: boolean;
  dimmerValue: string;
  executeValue?: string;
  curtainControl?: "quick" | "precise";
  button?: string;
  brightness?: number; // air driver only, 1-100
  temperature?: number; // air driver only, 2700-6500
  blasterMode?: "cool" | "auto" | "fan only" | "heat";
  blasterFan?: "1" | "2" | "3" | "4" | "5" ;
  blasterSwing?: "auto" | "h-swing" | "v-swing";
  category: CategoryKey;
}

type DeviceControlProps = {
  category: CategoryKey;
  itemId: string;
  selected?: SelectedItem;
  switchType?: number;
  onUpdate: (itemId: string, updates: Partial<SelectedItem>) => void;
};

const AIR_DRIVER_BRIGHTNESS_MIN = 1;
const AIR_DRIVER_BRIGHTNESS_MAX = 100;
const AIR_DRIVER_TEMP_MIN = 2700;
const AIR_DRIVER_TEMP_MAX = 6500;
const AIR_DRIVER_TEMP_STEP = 100;
const AIR_DRIVER_DEFAULT_BRIGHTNESS = 100;
const AIR_DRIVER_DEFAULT_TEMP = 4000;

const buildCurtainExecuteValue = (button: string, dimmerValue: string) => {
  return `${button}${dimmerValue}1`;
};

const decimalToHex2 = (value: number) => {
  return value.toString(16).toUpperCase().padStart(2, "0");
};

const DeviceControl = ({
  category,
  itemId,
  selected,
  switchType,
  onUpdate,
}: DeviceControlProps) => {
  if (!selected) return null;

   //  CHIP FAN CONTROL

  if (category === "switch" && switchType === 1) {
    const fanLevel = Number(selected.dimmerValue || 1);

    return (
      <div className="mt-2 ms-4">
        <div
          className="text-muted mb-1"
          style={{ fontSize: 12 }}
        >
          Fan Speed
        </div>

        <div className="d-flex gap-2">
          {[1, 2, 3, 4].map((level) => (
            <button
              key={level}
              type="button"
              className={`btn btn-sm ${
                fanLevel === level
                  ? "btn-primary"
                  : "btn-light border"
              }`}
              onClick={() =>
                onUpdate(itemId, {
                  dimmerValue: String(level),
                })
              }
            >
              {level}
            </button>
          ))}
        </div>
      </div>
    );
  }

    //  CHIP DIMMER CONTROL

  if (category === "switch" && switchType === 2) {
    const dimmerValue = Number(selected.dimmerValue || 1);

    return (
      <div className="mt-2 ms-4">
        <div className="d-flex justify-content-between">
          <span
            className="text-muted"
            style={{ fontSize: 12 }}
          >
            Brightness
          </span>

          <span
            className="fw-semibold"
            style={{ fontSize: 12 }}
          >
            {dimmerValue}
          </span>
        </div>

        <input
          type="range"
          min="1"
          max="10"
          step="1"
          value={dimmerValue}
          className="form-range"
          onChange={(e) =>
            onUpdate(itemId, {
              dimmerValue: e.target.value,
            })
          }
        />
      </div>
    );
  }

    //  AIR CURTAIN CONTROL
  if (category === "aircurtain") {
    const position = selected.dimmerValue
      ? parseInt(selected.dimmerValue, 16)
      : 100;

    return (
      <div className="mt-3 ms-4">

        {/* CONTROL MODE */}
        <div className="d-flex gap-3 mb-3">

          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id={`quick-${itemId}`}
              checked={selected.curtainControl === "quick"}
              onChange={() =>
                onUpdate(itemId, {
                  curtainControl: "quick",
                  isOn: true,
                  isDimmable: true,
                  dimmerValue: "00",
                  executeValue: "01001",
                  button: "01",
                })
              }
            />

            <label
              className="form-check-label"
              htmlFor={`quick-${itemId}`}
            >
              Quick Control
            </label>
          </div>

          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id={`precise-${itemId}`}
              checked={selected.curtainControl === "precise"}
              onChange={() =>
                onUpdate(itemId, {
                  curtainControl: "precise",
                  isOn: true,
                  isDimmable: true,
                  dimmerValue: "64",
                  executeValue: "02641",
                  button: "02",
                })
              }
            />

            <label
              className="form-check-label"
              htmlFor={`precise-${itemId}`}
            >
              Precise Control
            </label>
          </div>
        </div>

         {/* QUICK CONTROL  */}
        {selected.curtainControl === "quick" && (
          <div className="d-flex gap-2">

            <button
              type="button"
              className={`btn btn-sm ${
                selected.isOn
                  ? "btn-primary"
                  : "btn-light border"
              }`}
              onClick={() =>
                onUpdate(itemId, {
                  isOn: true,
                  dimmerValue: "00",
                  executeValue: "01001",
                  button: "01",
                })
              }
            >
              Open
            </button>

            <button
              type="button"
              className={`btn btn-sm ${
                !selected.isOn
                  ? "btn-primary"
                  : "btn-light border"
              }`}
              onClick={() =>
                onUpdate(itemId, {
                  isOn: false,
                  dimmerValue: "02",
                  executeValue: "01021",
                  button: "01",
                })
              }
            >
              Close
            </button>

          </div>
        )}

        {/* PRECISE CONTROL */}
        {selected.curtainControl === "precise" && (
          <div>
            <div className="d-flex justify-content-between">
              <span
                className="text-muted"
                style={{ fontSize: 12 }}
              >
                Curtain Position
              </span>

              <strong style={{ fontSize: 12 }}>
                {position}%
              </strong>
            </div>

            <input
              type="range"
              min="1"
              max="100"
              step="1"
              value={position}
              className="form-range"
              onChange={(e) => {
                const value = Number(e.target.value);
                const hex = decimalToHex2(value);

                onUpdate(itemId, {
                  isOn: true,
                  isDimmable: true,
                  dimmerValue: hex,
                  executeValue: buildCurtainExecuteValue(
                    "02",
                    hex,
                  ),
                  curtainControl: "precise",
                  button: "02",
                });
              }}
            />
          </div>
        )}
      </div>
    );
  }

    //  AIR DRIVER CONTROL
  if (category === "airdriver") {
    const brightness =
      selected.brightness ?? AIR_DRIVER_DEFAULT_BRIGHTNESS;

    const temperature =
      selected.temperature ?? AIR_DRIVER_DEFAULT_TEMP;

    return (
      <div className="mt-2 ms-4">

        {/* BRIGHTNESS */}
        <div className="d-flex justify-content-between">
          <span
            className="text-muted"
            style={{ fontSize: 12 }}
          >
            Brightness
          </span>

          <span
            className="fw-semibold"
            style={{ fontSize: 12 }}
          >
            {brightness}%
          </span>
        </div>

        <input
          type="range"
          min={AIR_DRIVER_BRIGHTNESS_MIN}
          max={AIR_DRIVER_BRIGHTNESS_MAX}
          step="1"
          value={brightness}
          className="form-range mb-2"
          onChange={(e) =>
            onUpdate(itemId, {
              brightness: Number(e.target.value),
            })
          }
        />

        {/* COLOR TEMPERATURE */}
        <div className="d-flex justify-content-between">
          <span
            className="text-muted"
            style={{ fontSize: 12 }}
          >
            Color Temperature
          </span>

          <span
            className="fw-semibold"
            style={{ fontSize: 12 }}
          >
            {temperature}K
          </span>
        </div>

        <input
          type="range"
          min={AIR_DRIVER_TEMP_MIN}
          max={AIR_DRIVER_TEMP_MAX}
          step={AIR_DRIVER_TEMP_STEP}
          value={temperature}
          className="form-range"
          onChange={(e) =>
            onUpdate(itemId, {
              temperature: Number(e.target.value),
            })
          }
        />
      </div>
    );
  }

  return null;
};

export default DeviceControl;